/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useEffect } from 'react';
import { useField } from 'formik';

export const CustomTextArea = ({
  labelText,
  requiredStar,
  autoResize,
  className,
  ...props
}) => {
  const [field, meta] = useField(props);
  const textareaRef = useRef(null);
  const lastHeightRef = useRef(null);
  const lastWidthRef = useRef(null);

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const nextHeight = el.scrollHeight;
    el.style.height = `${nextHeight}px`;

    if (nextHeight !== lastHeightRef.current) {
      lastHeightRef.current = nextHeight;
      el.scrollIntoView({ block: 'nearest' });
    }
  };

  useEffect(() => {
    if (autoResize) resize();
  }, [autoResize, field.value]);

  // A field's width can keep changing after mount — e.g. a parent modal
  // animating from a narrower to a wider layout — which reflows the text and
  // makes the height calculated above stale. Re-measure whenever the
  // textarea's own width actually changes (filtered so the height changes
  // resize() itself makes don't retrigger this).
  useEffect(() => {
    if (!autoResize) return undefined;
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (width !== lastWidthRef.current) {
        lastWidthRef.current = width;
        // Defer off the observer's own notification cycle — resize() forces
        // a layout read (scrollHeight), and doing that synchronously inside
        // a ResizeObserver callback is what triggers the browser's "loop
        // completed with undelivered notifications" warning.
        requestAnimationFrame(resize);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoResize]);

  return (
    <div className="formik-custom-field act-inputs-new">
      <label
        className={requiredStar ? 'required-stars' : ''}
        htmlFor={props.id || props.name}
      >
        {labelText}
      </label>
      <textarea
        ref={textareaRef}
        {...field}
        {...props}
        className={[
          className,
          autoResize && 'auto-resize-textarea',
          autoResize && props.disabled && 'auto-resize-textarea--disabled',
        ]
          .filter(Boolean)
          .join(' ')}
        error={meta.touched ? meta.error : 'false'}
      />
      {meta.touched && meta.error && (
        <div className="invalid-feedback">{meta.error}</div>
      )}
    </div>
  );
};
